import React, { useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

const chf = (v) => Number(v || 0).toFixed(2);
const addMonths = (iso, n) => { const d = new Date(iso); d.setMonth(d.getMonth() + n); return d.toISOString().slice(0, 10); };

/** Ratenplan manuell anlegen/bearbeiten: Anzahl + 1. Fälligkeit → gleiche Raten, danach frei editierbar. */
export function PaymentPlanDialog({ open, onOpenChange, bill, onSaved }) {
  const total = Number(bill?.amount_total || bill?.total || 0);
  const [count, setCount] = useState(3);
  const [first, setFirst] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !bill) return;
    const existing = bill.payment_plan?.installments;
    if (existing?.length) { setRows(existing.map((i) => ({ due_date: i.due_date, amount: i.amount }))); setCount(existing.length); setFirst(existing[0].due_date); }
    else { const f = (bill.due_date || new Date().toISOString().slice(0, 10)).slice(0, 10); setFirst(f); setRows(generate(3, f)); setCount(3); }
  }, [open, bill]); // eslint-disable-line

  const generate = (n, f) => {
    const base = Math.floor((total / n) * 100) / 100;
    const out = Array.from({ length: n }, (_, i) => ({ due_date: addMonths(f, i), amount: base }));
    out[n - 1].amount = Number((total - base * (n - 1)).toFixed(2));
    return out;
  };
  const sum = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const ok = Math.abs(sum - total) <= 0.05 && rows.length >= 2;

  const save = async () => {
    setBusy(true);
    try {
      await apiClient.put(`/bills/${bill._id}/payment-plan`, { installments: rows.map((r) => ({ due_date: r.due_date, amount: Number(r.amount) })) });
      toast.success(`Ratenplan mit ${rows.length} Raten gespeichert`);
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await apiClient.delete(`/bills/${bill._id}/payment-plan`); toast.success("Ratenplan entfernt"); onSaved?.(); onOpenChange(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Entfernen fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  if (!bill) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="payment-plan-dialog">
        <DialogHeader>
          <DialogTitle>Ratenplan {bill.payment_plan ? "bearbeiten" : "anlegen"}</DialogTitle>
          <DialogDescription>{bill.supplier_name_hint || bill.bill_number} · Rechnungsbetrag CHF {chf(total)} – die Raten müssen zusammen den Rechnungsbetrag ergeben.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2 items-end text-xs">
          <label className="space-y-1"><span className="text-slate-500">Anzahl Raten</span><Input type="number" min={2} max={36} value={count} onChange={(e) => setCount(Number(e.target.value))} data-testid="payment-plan-count" /></label>
          <label className="space-y-1"><span className="text-slate-500">1. Fälligkeit</span><SwissDateField  value={first} onChange={(e) => setFirst(e.target.value)} data-testid="payment-plan-first" /></label>
          <Button variant="outline" size="sm" onClick={() => setRows(generate(Math.max(2, count), first))} data-testid="payment-plan-generate">Gleichmässig verteilen</Button>
        </div>
        <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="payment-plan-rows">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center text-xs">
              <span className="col-span-2 text-slate-500">Rate {i + 1}</span>
              <SwissDateField  wrapClassName="col-span-5" className="h-8" value={r.due_date} data-testid={`payment-plan-row-date-${i}`} onChange={(e) => setRows(rows.map((x, n) => (n === i ? { ...x, due_date: e.target.value } : x)))} />
              <Input type="number" step="0.05" className="col-span-4 h-8 text-right" value={r.amount} onChange={(e) => setRows(rows.map((x, n) => (n === i ? { ...x, amount: e.target.value } : x)))} />
              <button type="button" className="col-span-1 text-slate-300 hover:text-rose-600" onClick={() => setRows(rows.filter((_, n) => n !== i))}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <button type="button" className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" onClick={() => setRows([...rows, { due_date: addMonths(rows[rows.length - 1]?.due_date || first, 1), amount: 0 }])}><Plus className="h-3.5 w-3.5" /> Rate</button>
        </div>
        <p className={`text-xs ${ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="payment-plan-sum">Summe Raten CHF {chf(sum)} / Rechnung CHF {chf(total)}{ok ? " ✓" : ` – Differenz ${chf(sum - total)}`}</p>
        <DialogFooter className="gap-2">
          {bill.payment_plan && <Button variant="ghost" className="text-rose-700" onClick={remove} disabled={busy} data-testid="payment-plan-delete">Plan entfernen</Button>}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={save} disabled={!ok || busy} data-testid="payment-plan-save">Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
